/**
 * Patient Card + directory API client (EPIC PATIENT-CARD, F3) — binds the
 * FROZEN API CONTRACT §1.1–§1.3 verbatim
 * (state/epics/PATIENT-CARD/plan/build-plan.md). Backend (auth/org resolved
 * server-side; ADMIN-only role gate on every route below):
 *
 *   GET /api/organisation/patients/{patient_id}
 *   GET /api/organisation/patients/{patient_id}/clinical-record?sections=
 *   GET /api/organisation/patients/directory?q=&mode=&limit=&cursor=
 *
 * Wire error shape is `{"detail":{"error_code":"..."}}` (§1.0, pinned) — this
 * module reads `resp.data.detail.error_code`, never `.message` prose (the
 * backend emits no display copy on these routes, INV-13; fallback strings
 * below are FE-owned copy, not server text). Every function returns
 * `{ success, data?, error?, code?, ...extras }`.
 *
 * This module makes NO formatting/display decisions (dates, money, section
 * labels) — that belongs to the components consuming it (F4–F8). It only
 * shapes requests and unwraps the envelope/error. It never calls a legacy
 * document `/pdf` route (those live in `admissionDischargeApi.js` and are
 * untouched by this file).
 */
import apiClient from './apiClient';

const PATIENTS_BASE = '/api/organisation/patients';
const enc = encodeURIComponent;

/**
 * §1.0 error envelope → `{ error, code, ...extras }`. `code` carries the
 * server's machine `error_code`; every other `detail` field (e.g.
 * `required_roles`/`actual_role` on 403, `unknown`/`supported` on 422
 * UNKNOWN_SECTION, `min_length` on 422 QUERY_TOO_SHORT) passes through as an
 * extra so a caller can render specifics without this module inventing a
 * per-code branch for each one.
 */
function parsePatientCardError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.error_code) {
    const { error_code, ...extras } = detail;
    return { error: fallback, code: error_code, ...extras };
  }
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission for this action.' };
    case 404: return { error: 'Patient not found.' };
    default:  return { error: fallback };
  }
}

/** §1.1 GET /patients/{id} — thin identity resource, no query params. */
export const getPatient = async (patientId) => {
  try {
    const response = await apiClient.get(`${PATIENTS_BASE}/${enc(patientId)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[PatientCardAPI] getPatient error:', error);
    return { success: false, ...parsePatientCardError(error, 'Failed to load the patient.') };
  }
};

/**
 * §1.2 GET /patients/{id}/clinical-record — the card envelope.
 *
 * `sections` (array of the closed §1.2 names, see `constants/patientCard.js
 * #SECTIONS`) is OPTIONAL — omit it (or pass an empty/undefined value) to
 * get the server's default set (R1-3: blank/omitted ⇒ default, identical
 * behaviour). This module never sends a client-guessed default in its
 * place — the server's default set is the single source of truth.
 */
export const getClinicalRecord = async (patientId, { sections } = {}) => {
  try {
    const params = {};
    if (Array.isArray(sections) && sections.length > 0) {
      params.sections = sections.join(',');
    }
    const response = await apiClient.get(
      `${PATIENTS_BASE}/${enc(patientId)}/clinical-record`,
      { params },
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[PatientCardAPI] getClinicalRecord error:', error);
    return { success: false, ...parsePatientCardError(error, 'Failed to load the clinical record.') };
  }
};

/**
 * §1.3 GET /patients/directory — search. `q` is REQUIRED by the contract
 * (min length 3 after trim, server-enforced — this module does not
 * pre-validate; the search box UI owns that affordance). `mode` defaults
 * server-side to `AUTO` when omitted; `limit`/`cursor` are passed through
 * only when the caller supplies them.
 */
export const getPatientDirectory = async ({ q, mode, limit, cursor } = {}) => {
  try {
    const params = { q };
    if (mode !== undefined) params.mode = mode;
    if (limit !== undefined) params.limit = limit;
    if (cursor !== undefined) params.cursor = cursor;
    const response = await apiClient.get(`${PATIENTS_BASE}/directory`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[PatientCardAPI] getPatientDirectory error:', error);
    return { success: false, ...parsePatientCardError(error, 'Failed to search patients.') };
  }
};

const patientCardApi = {
  getPatient,
  getClinicalRecord,
  getPatientDirectory,
};

export default patientCardApi;
